(() => {
  'use strict';

  const VERSION = '1.5.0';

  if (window.RFNProductCardRows && window.RFNProductCardRows.initialized) {
    if (window.RFNProductCardRows.version === VERSION) {
      window.RFNProductCardRows.refresh();
      return;
    }

    if (typeof window.RFNProductCardRows.destroy === 'function') {
      window.RFNProductCardRows.destroy();
    }
  }

  const COLLECTION_GRID_SELECTOR = [
    'body.template-collection .collection-grid',
    'body.template-search .collection-grid',
    '[data-rfn-card-rows-scope="search"] .collection-grid'
  ].join(', ');
  const SIGNATURE_TRACK_SELECTOR = '[data-rfn-card-group="signature"]';
  const CARD_SELECTOR = '.rfn-product-card[data-rfn-card-row-item]';
  const TRACK_NAMES = ['title', 'tagline', 'detail', 'editorial', 'price'];
  const OPTIONAL_TRACKS = ['tagline', 'detail', 'editorial'];
  const ROW_TOLERANCE = 6;

  let frameId = 0;
  let timerId = 0;
  let initialized = false;

  const getTrack = (card, name) =>
    card.querySelector(`[data-rfn-card-track="${name}"]`);

  const isVisible = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const clearCard = (card) => {
    OPTIONAL_TRACKS.forEach((name) => {
      card.classList.remove(`rfn-row-has-${name}`);
    });

    TRACK_NAMES.forEach((name) => {
      const track = getTrack(card, name);
      if (track) track.style.minHeight = '';
    });
  };

  const groupCardsByVisualRow = (cards) => {
    const sorted = [...cards].sort((a, b) => {
      const aRect = a.getBoundingClientRect();
      const bRect = b.getBoundingClientRect();

      return Math.abs(aRect.top - bRect.top) <= ROW_TOLERANCE
        ? aRect.left - bRect.left
        : aRect.top - bRect.top;
    });

    const rows = [];

    sorted.forEach((card) => {
      const top = card.getBoundingClientRect().top;
      const lastRow = rows[rows.length - 1];

      if (!lastRow || Math.abs(lastRow.top - top) > ROW_TOLERANCE) {
        rows.push({ top, cards: [card] });
      } else {
        lastRow.cards.push(card);
      }
    });

    return rows.map((row) => row.cards);
  };

  const groupSignatureCards = (cards, size) => {
    const safeSize = Math.max(1, size);
    if (safeSize === 1 || cards.length <= safeSize) return chunkIntoGroups(cards, safeSize);

    const pageGroups = chunkIntoGroups(cards, safeSize);
    const lastPageStart = Math.max(0, cards.length - safeSize);
    const lastPage = cards.slice(lastPageStart);
    const currentLastPage = pageGroups[pageGroups.length - 1] || [];

    // Embla trimSnaps keeps the final viewport full. For a remainder page,
    // the last visible page overlaps the previous source-order group.
    if (currentLastPage[0] !== lastPage[0]) {
      pageGroups.push(lastPage);
    }

    // Merge overlapping page groups so a card never receives conflicting
    // track heights when it appears in more than one carousel page.
    const merged = [];

    pageGroups.forEach((group) => {
      const overlapping = merged.filter((existing) =>
        existing.some((card) => group.includes(card))
      );

      if (!overlapping.length) {
        merged.push([...group]);
        return;
      }

      const union = new Set(group);
      overlapping.forEach((existing) => existing.forEach((card) => union.add(card)));

      const firstIndex = merged.indexOf(overlapping[0]);
      overlapping.forEach((existing) => {
        const index = merged.indexOf(existing);
        if (index >= 0) merged.splice(index, 1);
      });

      merged.splice(firstIndex, 0, cards.filter((card) => union.has(card)));
    });

    return merged;
  };

  function chunkIntoGroups(cards, size) {
    const groups = [];
    const safeSize = Math.max(1, size);

    for (let index = 0; index < cards.length; index += safeSize) {
      groups.push(cards.slice(index, index + safeSize));
    }

    return groups;
  }

  const rowHasContent = (row, name) =>
    row.some((card) => {
      const track = getTrack(card, name);
      return track && track.dataset.rfnHasContent === 'true';
    });

  const prepareRow = (row) => {
    OPTIONAL_TRACKS.forEach((name) => {
      const hasContent = rowHasContent(row, name);

      row.forEach((card) => {
        card.classList.toggle(`rfn-row-has-${name}`, hasContent);
      });
    });
  };

  const alignTrack = (row, name) => {
    const tracks = row
      .map((card) => getTrack(card, name))
      .filter(Boolean);

    if (!tracks.length) return;

    const maxHeight = Math.max(
      ...tracks.map((track) => Math.ceil(track.getBoundingClientRect().height))
    );

    tracks.forEach((track) => {
      track.style.minHeight = `${maxHeight}px`;
    });
  };

  const alignGroups = (container, groups) => {
    groups.forEach(prepareRow);

    // Recalculate after optional-track spacing classes are applied.
    void container.offsetHeight;

    groups.forEach((group) => {
      TRACK_NAMES.forEach((name) => alignTrack(group, name));
    });
  };

  const alignCollectionGrid = (grid) => {
    const allCards = [...grid.querySelectorAll(CARD_SELECTOR)];
    allCards.forEach(clearCard);

    if (grid.dataset.view === 'list') return;

    const cards = allCards.filter(isVisible);
    if (!cards.length) return;

    alignGroups(grid, groupCardsByVisualRow(cards));
  };

  const getSignatureGroupSize = (track) => {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const rawValue = mobile
      ? track.dataset.rfnCardsMobile
      : track.dataset.rfnCardsDesktop;
    const parsed = Number.parseInt(rawValue || (mobile ? '1' : '3'), 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const alignSignatureTrack = (track) => {
    const allCards = [...track.querySelectorAll(CARD_SELECTOR)];
    allCards.forEach(clearCard);

    if (!isVisible(track)) return;

    const cards = allCards.filter(isVisible);
    if (!cards.length) return;

    alignGroups(track, groupSignatureCards(cards, getSignatureGroupSize(track)));
  };

  const refresh = () => {
    frameId = 0;

    document.querySelectorAll(COLLECTION_GRID_SELECTOR).forEach(alignCollectionGrid);
    document.querySelectorAll(SIGNATURE_TRACK_SELECTOR).forEach(alignSignatureTrack);
  };

  const schedule = (delay = 0) => {
    window.clearTimeout(timerId);

    timerId = window.setTimeout(() => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(refresh);
    }, delay);
  };

  const mutationTouchesCards = (mutation) => {
    const target = mutation.target instanceof Element
      ? mutation.target
      : mutation.target.parentElement;

    if (
      target &&
      target.closest('#CollectionAjaxResult, [data-rfn-signatures], [data-rfn-card-group="signature"]')
    ) {
      return true;
    }

    return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
      if (!(node instanceof Element)) return false;

      return (
        node.matches(
          '#CollectionAjaxResult, .collection-grid, [data-rfn-signatures], [data-rfn-card-group="signature"], .rfn-product-card'
        ) ||
        node.querySelector(
          '#CollectionAjaxResult, .collection-grid, [data-rfn-signatures], [data-rfn-card-group="signature"], .rfn-product-card'
        )
      );
    });
  };

  const observer = new MutationObserver((mutations) => {
    if (mutations.some(mutationTouchesCards)) schedule(40);
  });

  const onResize = () => schedule(90);
  const onGridViewClick = (event) => {
    if (event.target.closest('.grid-view-btn')) {
      schedule(40);
      schedule(280);
    }
  };
  const onExternalRefresh = () => schedule(20);

  const init = () => {
    if (initialized) return;
    initialized = true;

    schedule();

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', onExternalRefresh, { once: true });
    window.addEventListener('pageshow', onExternalRefresh);

    document.addEventListener('click', onGridViewClick);
    document.addEventListener('shopify:section:load', onExternalRefresh);
    document.addEventListener('collection:reloaded', onExternalRefresh);
    document.addEventListener('search:reloaded', onExternalRefresh);
    document.addEventListener('rfn:product-card-rows:refresh', onExternalRefresh);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => schedule(20));
    }
  };

  window.RFNProductCardRows = {
    initialized: true,
    version: VERSION,
    refresh: () => schedule(),
    destroy: () => {
      observer.disconnect();
      window.clearTimeout(timerId);
      if (frameId) window.cancelAnimationFrame(frameId);

      window.removeEventListener('resize', onResize);
      window.removeEventListener('pageshow', onExternalRefresh);
      document.removeEventListener('click', onGridViewClick);
      document.removeEventListener('shopify:section:load', onExternalRefresh);
      document.removeEventListener('collection:reloaded', onExternalRefresh);
      document.removeEventListener('search:reloaded', onExternalRefresh);
      document.removeEventListener('rfn:product-card-rows:refresh', onExternalRefresh);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
